/**
 * Backend-neutral camera: the `neutral` member of a camera token
 * (VisualizerInterface.GetCameraPosition), so a view survives a live switch of the 3D viewer and
 * a saved session restores its camera in either backend.
 *
 *   center    [x, y, z]  the model point on the view axis (the centre of rotation)
 *   rotation  [9]        model -> camera rotation, row-major
 *   distance  number     camera to center, along the view axis (Angstrom)
 *   fov       number     vertical field of view in degrees
 *
 * Both backends use a perspective camera looking down -z with +y up and project with the
 * vertical field of view and the canvas aspect ratio (3Dmol: a three.js-style camera at
 * (0, 0, CAMERA_Z) looking at the origin; PyMOL: SceneRender / pymol-web js/projection.js), so
 * the same neutral camera frames the same picture in both when the field of view agrees
 * (20 degrees in both by default). Clipping planes (slab) are not part of it.
 *
 * Pure functions, no imports: used by both backends and by mocha tests.
 */

/** @returns {boolean} whether `c` is a usable neutral camera */
export function isNeutralCamera(c) {
    return !!c
        && Array.isArray(c.center) && c.center.length === 3 && c.center.every(Number.isFinite)
        && Array.isArray(c.rotation) && c.rotation.length === 9 && c.rotation.every(Number.isFinite)
        && Number.isFinite(c.distance) && c.distance > 0
        && Number.isFinite(c.fov) && c.fov > 0;
}

/** Rotation matrix (row-major 3x3) of a unit quaternion, three.js convention (rotates vectors). */
export function quaternionToMatrix({
    x, y, z, w,
}) {
    const x2 = x + x; const y2 = y + y; const z2 = z + z;
    const xx = x * x2; const xy = x * y2; const xz = x * z2;
    const yy = y * y2; const yz = y * z2; const zz = z * z2;
    const wx = w * x2; const wy = w * y2; const wz = w * z2;
    return [
        1 - (yy + zz), xy - wz, xz + wy,
        xy + wz, 1 - (xx + zz), yz - wx,
        xz - wy, yz + wx, 1 - (xx + yy),
    ];
}

/** Unit quaternion of a rotation matrix (row-major 3x3), three.js setFromRotationMatrix. */
export function matrixToQuaternion(m) {
    const [m11, m12, m13, m21, m22, m23, m31, m32, m33] = m;
    const trace = m11 + m22 + m33;
    let s;
    if (trace > 0) {
        s = 0.5 / Math.sqrt(trace + 1);
        return {
            w: 0.25 / s, x: (m32 - m23) * s, y: (m13 - m31) * s, z: (m21 - m12) * s,
        };
    }
    if (m11 > m22 && m11 > m33) {
        s = 2 * Math.sqrt(1 + m11 - m22 - m33);
        return {
            w: (m32 - m23) / s, x: 0.25 * s, y: (m12 + m21) / s, z: (m13 + m31) / s,
        };
    }
    if (m22 > m33) {
        s = 2 * Math.sqrt(1 + m22 - m11 - m33);
        return {
            w: (m13 - m31) / s, x: (m12 + m21) / s, y: 0.25 * s, z: (m23 + m32) / s,
        };
    }
    s = 2 * Math.sqrt(1 + m33 - m11 - m22);
    return {
        w: (m21 - m12) / s, x: (m13 + m31) / s, y: (m23 + m32) / s, z: 0.25 * s,
    };
}

/** R^T * v for a row-major 3x3 R */
function transposeTimes(r, [a, b, c]) {
    return [
        r[0] * a + r[3] * b + r[6] * c,
        r[1] * a + r[4] * b + r[7] * c,
        r[2] * a + r[5] * b + r[8] * c,
    ];
}

// ------------------------------------------------------------------------------------ PyMOL

/**
 * PyMOL 18-float view (cmd.get_view; v[0..8] column-major model -> camera rotation, v[9..11]
 * camera-space translation, v[12..14] origin of rotation, v[15]/v[16] front/back clipping
 * distances, v[17] = -fov (perspective) or +fov (orthoscopic)) -> neutral camera.
 */
export function neutralFromPyMOLView(v) {
    const rotation = [v[0], v[3], v[6], v[1], v[4], v[7], v[2], v[5], v[8]];
    // the model point that the camera-space translation puts on the view axis
    const off = transposeTimes(rotation, [-v[9], -v[10], 0]);
    return {
        center: [v[12] + off[0], v[13] + off[1], v[14] + off[2]],
        rotation,
        distance: -v[11],
        fov: Math.abs(v[17]) > 1 ? Math.abs(v[17]) : 20,
    };
}

/**
 * Neutral camera -> PyMOL view. The clipping planes and the projection flag come from `current`
 * (PyMOL's view right now, e.g. after a zoom to the scene): the slab keeps its depth span and is
 * centred on the new distance. PyMOL's own field of view is kept (both default to 20 degrees).
 * @param {object} camera neutral camera
 * @param {number[]} current PyMOL's current 18-float view
 */
export function pymolViewFromNeutral(camera, current) {
    const r = camera.rotation;
    const d = camera.distance;
    const half = Math.max(5, (current[16] - current[15]) / 2);
    return [
        r[0], r[3], r[6], r[1], r[4], r[7], r[2], r[5], r[8],
        0, 0, -d,
        camera.center[0], camera.center[1], camera.center[2],
        Math.max(1, d - half), d + half,
        current[17],
    ];
}

// ------------------------------------------------------------------------------------ 3Dmol

/**
 * 3Dmol GLViewer state -> neutral camera. A model point p is at
 * R(q) (p + modelGroup.position) + rotationGroup.position in world space; the camera sits at
 * (cameraX, 0, cameraZ) looking at the origin.
 * @param {{modelPosition: {x,y,z}, rotationPosition: {x,y,z}, quaternion: {x,y,z,w},
 *          cameraX?: number, cameraZ: number, fov: number}} s
 */
export function neutralFrom3Dmol({
    modelPosition, rotationPosition, quaternion, cameraX=0, cameraZ, fov,
}) {
    const rotation = quaternionToMatrix(quaternion);
    // p0 on the view axis: R (p0 + m) + rp - cam = (0, 0, -distance)
    const off = transposeTimes(rotation, [cameraX - rotationPosition.x, -rotationPosition.y, 0]);
    return {
        center: [off[0] - modelPosition.x, off[1] - modelPosition.y, off[2] - modelPosition.z],
        rotation,
        distance: cameraZ - rotationPosition.z,
        fov,
    };
}

/**
 * Neutral camera -> 3Dmol GLViewer state (rotationGroup on the camera axis).
 * @returns {{modelPosition: {x,y,z}, rotationPosition: {x,y,z}, quaternion: {x,y,z,w}}}
 */
export function threeDmolFromNeutral(camera, { cameraX=0, cameraZ }) {
    return {
        modelPosition: { x: -camera.center[0], y: -camera.center[1], z: -camera.center[2] },
        rotationPosition: { x: cameraX, y: 0, z: cameraZ - camera.distance },
        quaternion: matrixToQuaternion(camera.rotation),
    };
}
